import moment from "moment";
import Kichiri from "./KichiriService";
import UserService from "./UserService";
import WORKFLOW from "../constants/Workflow";
import UtilityService from "./UtilityService";
import ConfigurationService from "../services/ConfigurationService";
import { DEFAULT_VIEWS } from "../constants/Views";

const EngagementService = {
	/**
	 * Filter all the tasks based on the search search
	 *
	 * @param {String} searchTerm
	 * @param {Array} tasks
	 * @return {Array}
	 */
	searchTasks({ searchTerm, tasks }) {
		searchTerm = searchTerm.toLowerCase();

		return tasks.filter(task => {
			let userLastName = task.User ? task.User.last_name.toLowerCase() : "unassigned";
			let userFirstName = task.User ? task.User.first_name.toLowerCase() : "unassigned";

			let name = task.name || "untitled";
			let description = task.description || "no description";
			let notes = task.notes || "no notes";

			let deadline = moment(task.deadline).format("MMMM Do YYYY @ h:mma") || "";

			return (
				name.toLowerCase().indexOf(searchTerm) !== -1 ||
				description.toLowerCase().indexOf(searchTerm) !== -1 ||
				userFirstName.toLowerCase().indexOf(searchTerm) !== -1 ||
				userLastName.toLowerCase().indexOf(searchTerm) !== -1 ||
				notes.toLowerCase().indexOf(searchTerm) !== -1 ||
				task.status.toLowerCase().indexOf(searchTerm) !== -1 ||
				deadline.toLowerCase().indexOf(searchTerm) !== -1
			);
		});
	},

	/**
	 * Update a single engagement
	 *
	 * @param {Object} request
	 * @return {Promise}
	 */
	async updateEngagement({ request }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.update(request, {}, authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return false;
	},

	/**
	 * Deletes an engagement
	 * @param {Number} engagementId
	 * @returns {Boolean}
	 */
	async deleteEngagement({ engagementId, shouldDeleteRecurringEngagement }) {
		let { authToken } = UserService.getUserData();

		try {
			await Kichiri.engagement.delete({ engagementId, shouldDeleteRecurringEngagement }, {}, authToken);

			return true;
		} catch (error) {
			console.log(error);
		}

		return false;
	},
	
	/**
	 * Deletes multiple engagements
	 * @param {Array<Number>} engagementIds
	 * @returns {Boolean}
	 */
	async deleteEngagements({ engagementIds }) {
		let { authToken } = UserService.getUserData();

		try {
			await Kichiri.engagement.multiDelete(
				{ engagementIds },
				{},
				authToken
			);

			return true;
		} catch (error) {
			console.log(error);
		}

		return false;
	},

	/**
	 * Fetch engagements
	 *
	 * @param {Object} params
	 * @returns {Object}
	 */
	async fetchEngagements(params) {
		let userData = UserService.getUserData();
		params.companyId = userData.companyId;
		if (params.assignedUsers) {
			params.assignedUsers = JSON.stringify(params.assignedUsers);
		}

		if (params.assignedClients) {
			params.assignedClients = JSON.stringify(params.assignedClients);
		}

		if (params.assignedClientGroups) {
			params.assignedClientGroups = JSON.stringify(params.assignedClientGroups);
		}

		if (params.assignedTeams) {
			params.assignedTeams = JSON.stringify(params.assignedTeams);
		}

		// if (params.assignedFlowStates) {
		// 	params.assignedFlowStates = JSON.stringify(params.assignedFlowStates);
		// }

		if (params.yearEnd) {
			params.yearEnd = JSON.stringify(params.yearEnd);
		}

		if (params.dueDate) {
			params.dueDate = JSON.stringify(params.dueDate);
		}

		if (params.lastActivityDate) {
			params.lastActivityDate = JSON.stringify(params.lastActivityDate);
		}

		if (params.dateIn) {
			params.dateIn = JSON.stringify(params.dateIn);
		}

		let updatedFlowStates = params.selectedFlowStates.map(flowState => ({ flowStateId: flowState.flowStateId }));

		if (params.selectedFlowStates) {
			params.selectedFlowStates = JSON.stringify(updatedFlowStates);
		}

		let query = {
			assignedUsers: "[]",
			assignedTeams: "[]",
			assignedClients: "[]",
			assignedClientGroups: "[]",
			// assignedFlowStates: "[]",
			selectedFlowStates: "[]",
			companyId: userData.companyId,
			clientId: 0,
			flowId: null,
			limit: 50,
			offset: 0,
			search: null,
			shouldShowInactive: false,
			sort: [{ field: WORKFLOW.orderQueries.fiscalYearEnd, order: "desc" }],
			countOnly: false
		};

		if (params) {
			query = Object.assign(query, params);
		}

		try {
			let response = await Kichiri.engagement.list({}, query, userData.authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Fetches list of flows
	 * @param {String} searchTerm
	 * @returns {Array}
	 */
	async fetchFlows({ searchTerm = "" }) {
		let userData = UserService.getUserData();

		let query = {
			companyId: userData.companyId
		};

		if (searchTerm) {
			query.searchTerm = searchTerm;
		}

		try {
			let response = await Kichiri.flow.list({}, query, userData.authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return [];
	},

	/**
	 * Fetches a flow
	 * @param {Number} flowId
	 * @returns {Object}
	 */
	async fetchFlow({ flowId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.flow.get({ flowId }, {}, userData.authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return [];
	},

	/**
	 * Fetches list of flow states
	 * @param {Number} flowId
	 * @param {String} flowCode
	 * @param {String} searchTerm
	 * @returns {Array}
	 */
	async fetchFlowStates({ flowId, flowCode, searchTerm }) {
		if (!flowCode) {
			return [];
		}

		let userData = UserService.getUserData();

		let query = {
			companyId: userData.companyId,
			flowId,
			flowCode,
			searchTerm
		};

		try {
			let response = await Kichiri.flow.listStates({}, query, userData.authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return [];
	},

	/**
	 * Fetch and engagement
	 * @param {Number} engagementId
	 * @returns {Object}
	 */
	async fetchEngagement(engagementId) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.get(
				{
					engagementId
				},
				{},
				userData.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Create multiple engagments
	 * @param {Number} clientIds
	 * @param {Object} flowIds
	 * @returns {Object}
	 */
	async multiCreateEngagements({ clientIds, flowIds }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.multiCreate(
				{
					clientIds,
					flowIds
				},
				{},
				userData.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	Create an engagement
	@param {Object} params - Engagement parameters
	@param {String} params.name - Engagement name
	@param {String} params.namingConvention - Engagement naming convention - Naming convention ID
	@param {Number} params.teamId - ID of the team
	@param {Number} params.clientId - ID of the client
	@param {Number} params.flowId - ID of the flow
	@param {Number} params.flowStateId - ID of the flow state
	@param {Date} params.dueDate - Due date of the engagement
	@param {Date} params.dateIn - Date in for the engagement
	@param {Date} params.fiscalYearEnd - Fiscal year-end date
	@param {Array} params.assignedUsers - List of assigned users
	@param {Boolean} params.taxEstimate - Indicates if it's a tax estimate
	@returns {Promise} - Returns a promise that resolves to the created engagement object
	*/
	async create({ name, namingConvention, teamId, clientId, flowId, flowStateId, dueDate, dateIn, fiscalYearEnd, assignedUsers, tasks, rrule, relativeDueDate, taxEstimate, budgets }) {
		let { authToken, companyId } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.create(
				{
					name,
					namingConvention,

					teamId,
					clientId,
					flowId,
					flowStateId,

					dueDate,
					dateIn,
					fiscalYearEnd,

					assignedUsers,
					tasks,
					budgets,
					rrule,
					// If there is an rrule string in creating the engagement, then it is a recurring engagement
					isRecurring: UtilityService.isNotNullish(rrule) ? rrule.length > 0 : false,
					relativeDueDate,
					taxEstimate,
					companyId
				},
				{},
				authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	async fetchView({ viewId }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.view.get(
				{
					viewId
				},
				{},
				authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}
	},

	async fetchViews({ type, layout }) {
		let { authToken, userId } = UserService.getUserData();

		try {
			let response = await Kichiri.view.list(
				{},
				{
					userId,
					type,
					layout
				},
				authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		// Return an empty list (not null) so callers can safely .map/.find.
		return [];
	},

	async createView({ name, userIds, teamIds, schema, isShared, assignedUsers, assignedTeams, type = "engagement" }) {
		let { authToken, companyId } = UserService.getUserData();

		try {
			let response = await Kichiri.view.create(
				{
					name,
					userIds,
					teamIds,
					schema,
					isShared,
					companyId,
					assignedUsers,
					assignedTeams,
					type
				},
				{},
				authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Fetch an engagement
	 * @param {Number} engagementId
	 * @returns {Object}
	 */
	async get({ engagementId }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.get({ engagementId }, {}, authToken);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Updates an engagement
	 * @param {Number} engagementId
	 * @param {Number} teamId
	 * @param {Number} clientId
	 * @param {Number} flowStateId
	 * @param {String} name
	 * @param {String} namingConvention
	 * @param {Date} dueDate
	 * @param {Date} dateIn
	 * @param {Date} fiscalYearEnd
	 * @param {String} notes
	 * @param {Array} assignedUsers
	 * @param {Boolean} taxEstimate
	 * @returns {Object}
	 */
	async update({ engagementId, teamId, clientId, flowStateId, name, namingConvention, dueDate, dateIn, fiscalYearEnd, notes, assignedUsers, taxEstimate }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.update(
				{ engagementId, teamId, clientId, flowStateId, name, namingConvention, dueDate, dateIn, fiscalYearEnd, notes, assignedUsers, taxEstimate },
				{},
				authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	async updateView({ viewId, schema, name, isShared, userIds, teamIds, status, type = "engagement" }) {
		let { authToken, companyId } = UserService.getUserData();

		try {
			let response = await Kichiri.view.update(
				{
					viewId,
					name,
					userIds,
					teamIds,
					schema,
					isShared,
					companyId,
					status,
					type
				},
				{
					viewId
				},
				authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Updates the view user with the provided view IDs.
	 * @param {Object} params - The parameters for updating the view user.
	 * @param {Array} params.viewIds - The IDs of the views to update.
	 * @returns {Promise<Object|null>} The response data from the update operation, or null if an error occurs.
	 */
	async updateViewUser({ viewIds }) {
		let { authToken, userId } = UserService.getUserData();

		try {
			let response = await Kichiri.view.updateViewUser(
				{
					userId,
					viewIds
				},
				{
					userId
				},
				authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Fetches a list of view user records
	 * @returns {Promise<Object|null>} The list view data of the user if the request is successful, otherwise null.
	 */
	async listViewUser() {
		let { authToken, userId } = UserService.getUserData();

		try {
			let response = await Kichiri.view.listViewUser({ userId }, {}, authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * XXX - Temporary custom function to derive due dates for different flow types
	 * We don't have an elegant solution right now, so for now we are going to write custom code for this
	 *
	 * @param {String} flowCode
	 * @param {Date} fiscalYearEnd
	 * @param {String} frequency
	 */
	deriveDueDateForEngagement({ flowCode, fiscalYearEnd, frequency }) {
		let date = moment();

		if (flowCode === "t1") {
			let newDate = moment(fiscalYearEnd);

			newDate.add(1, "years");
			newDate.set({ month: 3, date: 30 });

			date = newDate;
		} else if (flowCode === "t2") {
			date = moment(fiscalYearEnd).add(6, "months");
		} else if (flowCode === "t3") {
			date = moment(fiscalYearEnd).add(90, "days");
		} else if (flowCode === "t4" || flowCode === "t5") {
			let newDate = moment(fiscalYearEnd);

			newDate.add(1, "years");
			newDate.set({ month: 1, date: 28 });

			date = newDate;
		} else if (flowCode === "hst") {
			// Default behaviour
			date = moment(fiscalYearEnd).add(3, "months");

			if (frequency === "annual") {
				date = moment(fiscalYearEnd).add(3, "months");
			} else if (frequency === "quarterly") {
				date = moment(fiscalYearEnd).add(1, "months");
			} else if (frequency === "monthly") {
				date = moment(fiscalYearEnd).add(1, "months");
			}
		}

		return date.toDate();
	},

	async fetchEngagementTemplates({ searchTerm = "", sortField, sortOrder }) {
		try {
			let { authToken, companyId } = UserService.getUserData();

			let { data: templates } = await Kichiri.engagement.listTemplates({}, { companyId, searchTerm, sortField, sortOrder }, authToken);

			return templates;
		} catch (error) {
			console.log(error);
		}

		return [];
	},

	async fetchEngagementTemplate({ engagementTemplateId }) {
		try {
			let { authToken } = UserService.getUserData();

			let { data: template } = await Kichiri.engagement.getTemplate({ engagementTemplateId }, {}, authToken);

			return template;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	async updateEngagementTemplate({ engagementTemplateId, teamId, flowId, flowStateId, name, notes, fiscalYearEnd, dueDate, dateIn, tasks, users, status }) {
		try {
			let { authToken, companyId } = UserService.getUserData();

			let { data: template } = await Kichiri.engagement.updateTemplate(
				{ engagementTemplateId, companyId, teamId, flowId, flowStateId, name, notes, fiscalYearEnd, dueDate, dateIn, tasks, users, status },
				{},
				authToken
			);

			return template;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	async createEngagementTemplate({ teamId, flowId, flowStateId, name, notes, fiscalYearEnd, dueDate, dateIn, tasks, users, status }) {
		try {
			let { authToken, companyId } = UserService.getUserData();

			let { data: template } = await Kichiri.engagement.createTemplate(
				{ companyId, teamId, flowId, flowStateId, name, notes, fiscalYearEnd, dueDate, dateIn, tasks, users, status },
				{},
				authToken
			);

			return template;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Update Recurring Engagement
	 *
	 * @param {Number} recurringEngagementId
	 * @param {String} rrule
	 * @returns
	 */
	async updateRecurringEngagement({ recurringEngagementId, rrule, engagementSnapshot, engagementId }) {
		try {
			let { authToken } = UserService.getUserData();

			let { data: recurringEngagement } = await Kichiri.engagement.updateRecurringEngagement(
				{ recurringEngagementId, rrule, engagementSnapshot, engagementId },
				{},
				authToken
			);

			return recurringEngagement;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * A request to export engagements
	 * @param {number} clientId
	 * @param {number} flowId
	 * @param {Array} assignedUsers
	 * @param {Array} assignedTeams
	 * @param {Array} assignedFlowStates
	 * @param {Date} yearEnd
	 * @param {Date} dueDate
	 * @param {Date} dateIn
	 * @param {String} search
	 * @param {Number} limit
	 * @param {Number} offset
	 * @param {Array} sort
	 * @param {Array<Object>} selectedFlowStates
	 */
	async exportEngagements({
		clientId,
		flowId,
		assignedUsers,
		assignedTeams,
		selectedFlowStates,
		yearEnd,
		dueDate,
		dateIn,
		search,
		limit,
		offset,
		sort,
		lastActivityDate
	}) {
		let { authToken } = UserService.getUserData();

		let query = {
			clientId,
			flowId,
			assignedUsers,
			assignedTeams,
			selectedFlowStates,
			yearEnd,
			dueDate,
			dateIn,
			limit,
			offset,
			search,
			sort,
			lastActivityDate
		};

		let response = await fetch(`${ConfigurationService.resolveApi().rest}/engagement/export`, {
			method: "POST",
			body: JSON.stringify(query),
			headers: {
				"Content-Type": "application/json",
				authorization: authToken
			}
		});

		let blob = await response.blob();

		// Tis a ghetto little thing
		let url = window.URL.createObjectURL(blob);
		let a = document.createElement("a");
		a.href = url;
		a.target = "_blank";
		a.download = `workflow-engagement-export-${moment().format("YYYY-MM-DD")}.csv`;
		document.body.appendChild(a);
		a.click();
		a.remove();
	},

	/**
	 * Checks if a specific column is present in the view's columns
	 *
	 * @param {Object} columns - An object representing the columns in the view
	 * @param {string} columnId - The ID of the column to check for
	 * @returns {boolean} - True if the column is present or if columns is empty, false otherwise
	 */
	viewHasColumn({ columns, columnId }) {
		// eslint-disable-next-line no-undef
		if (_.isEmpty(columns)) {
			return true;
		}
		return columns[columnId];
	},

	/**
	 * Indicates if the view is a default static view (eg, All, or Assigned to Me)
	 *
	 * @param {String} viewCode
	 * @returns {Boolean}
	 */
	isDefaultViewStatic({ viewCode }) {
		return DEFAULT_VIEWS.find(defaultView => defaultView.code === viewCode);
	},

	/**
	 * Returns a list of flow states but flatten to make filtering and readabliity better for a specified flow
	 *
	 * @param {String} flowCode
	 * @returns {Array<Object>}
	 */
	async flattenFlowStates({ flowCode }) {
		let flowStates = await this.fetchFlowStates({ flowCode });

		flowStates = flowStates.map(flowState => ({
			flowId: flowState.flow_id,
			flowName: flowState.Flow.name,
			flowStateId: flowState.id,
			flowStateName: flowState.name,
			flowStateColor: flowState.color,
			code: flowState.Flow.code
		}));
		return flowStates;
	},

	/**
	Create a budget for an engagement
	@param {Object} params - budget parameters
	@param {String} params.engagementId - id of the engagement
	@param {String} params.resource - resource
	@param {Number} params.budget - budget (in time) 
	@returns {Promise} - Returns a promise that resolves to the created budget object
	*/
	async createBudget({ engagementId, resource, budget }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.createBudget(
				{
					engagementId,
					resource,
					budget
				},
				{},
				authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Updates a budget for a specific engagement
	 *
	 * @param {Object} params - Parameters for updating the budget
	 * @param {String} params.engagementId - ID of the engagement
	 * @param {String} params.field - The field to update in the budget
	 * @param {Number} params.value - The new value for the budget field
	 * @param {Number} params.index - The index of the budget item to update
	 * @returns {Promise} - Returns a promise that resolves to the updated budget object
	 */
	async updateBudget({ engagementId, field, value, index }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.updateBudget(
				{
					engagementId,
					field,
					value,
					index
				},
				{},
				authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Removes a budget item for a specific engagement
	 *
	 * @param {Object} params - Parameters for removing the budget
	 * @param {String} params.engagementId - ID of the engagement
	 * @param {Number} params.index - The index of the budget item to remove
	 * @returns {Promise} - Returns a promise that resolves to the response of the removal operation
	 */
	async removeBudget({ engagementId, index }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.removeBudget(
				{
					engagementId,
					index
				},
				{},
				authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Retrieves the budgets for a specific engagement
	 *
	 * @param {Object} params - Parameters for fetching the budgets
	 * @param {String} params.engagementId - ID of the engagement
	 * @returns {Promise} - Returns a promise that resolves to the budgets associated with the engagement
	 */
	async listBudgets({ engagementId }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.listBudgets({ engagementId }, {}, authToken);

			return response;
		} catch (error) {
			console.log(error);
		}
	},
		async listRecurringEngagements() {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.engagement.listRecurringEngagements({} ,{}, authToken);

			return response;
		} catch (error) {
			console.log(error);
		}
	}


};

export default EngagementService;
